import React, { useContext, useMemo } from "react";
import HSelect from "../custom/input/select/Select";
import UserProfileCard from "../../voice/components/common/UserProfileCard";
import TeamProfileCard from "../../voice/components/common/TeamProfileCard";
import { AppContext } from "../../context/AppContext";
import { Grid } from "antd";

const AssigneeSelect = ({
    value,
    onChange,
    teamsList = [],
    usersList,
    excludeUserIds = [],
    disabled = false,
    loading = false,
    variant = "header",
    tabIndex,
    autoFocus,
    optionsDisabled = false,
    "data-cy": dataCy = "conversation-header-assignee-select",
    groupBy = "group",
    customClassName = "hs-h-35-px",
}) => {
    const [appProperties] = useContext(AppContext) ?? [];
    const { md } = Grid.useBreakpoint();

    const placeholder = useMemo(
        () => (
            <div className="d-flex align-items-center gap-2">
                <div className="actionIconsSprite2 unassigned-icon" />
                {md ? "Unassigned" : null}
            </div>
        ),
        [md]
    );

    const isAssigneeExist = useMemo(() => {
        return (
            appProperties?.licenseObj?.allLicensedUserDetails?.some((user) => user.osyncUserId === value) ||
            teamsList?.some((team) => team.teamsEntity.teamId === value)
        );
    }, [appProperties?.licenseObj?.allLicensedUserDetails, teamsList, value]);

    const assignees = useMemo(() => {
        const sourceUsers = usersList ?? appProperties?.licenseObj?.allLicensedUserDetails ?? [];
        const users = sourceUsers.filter((u) => !excludeUserIds?.includes(u?.osyncUserId));
        const userOptions = users.map((user) => ({
            ...user,
            label: <UserProfileCard size="small" user={user} disabled={optionsDisabled} />,
            labelText:
                user?.firstName +
                " " +
                (user?.lastName ? user?.lastName + " (" + user?.email + ")" : "(" + user?.email + ")"),
            value: user.osyncUserId,
            selectedLabel: <UserProfileCard size="small" user={user} disabled={optionsDisabled} iconOnly={!md} />,
            userData: user,
            group: "Users",
        }));
        const teamOptions = (teamsList || []).map((team) => ({
            ...team?.teamsEntity,
            label: <TeamProfileCard size="small" team={team?.teamsEntity} disabled={optionsDisabled} />,
            labelText: team?.teamsEntity?.friendlyName,
            value: team?.teamsEntity?.teamId,
            teamData: team?.teamsEntity,
            selectedLabel: (
                <TeamProfileCard size="small" team={team?.teamsEntity} disabled={optionsDisabled} iconOnly={!md} />
            ),
            group: "Teams",
        }));
        return [...userOptions, ...teamOptions];
    }, [usersList, appProperties?.licenseObj?.allLicensedUserDetails, teamsList, optionsDisabled, excludeUserIds, md]);

    const isValueValid = useMemo(() => {
        if (!value) return false;
        const sourceUsers = usersList ?? appProperties?.licenseObj?.allLicensedUserDetails ?? [];
        const existsInUsers = sourceUsers?.some((u) => u.osyncUserId === value);
        const existsInTeams = assignees?.some((item) => item.value === value && item.group === "Teams");
        return existsInUsers || existsInTeams;
    }, [usersList, appProperties?.licenseObj?.allLicensedUserDetails, value, assignees]);

    const selectValue = isValueValid ? value : null;

    const isHeaderVariant = variant === "header";

    return (
        <HSelect
            data-cy={dataCy}
            bordered={!isHeaderVariant}
            size={isHeaderVariant ? "medium" : undefined}
            groupBy={groupBy}
            variant={isHeaderVariant ? "" : "underlined"}
            floatingLabel={false}
            customProps={{
                value: isAssigneeExist ? selectValue : null,
                suffixIcon: isHeaderVariant ? (
                    <div className="actionIconsSprite smallSize-fromNumberDropDownIcon" />
                ) : undefined,
                onChange: (val) => onChange?.(val),
                className: customClassName,
                tabIndex,
                autoFocus,
            }}
            popupMatchSelectWidth={!isHeaderVariant}
            placeholder={placeholder}
            options={assignees}
            loading={loading}
            disabled={loading || disabled}
            rootClassName="assignee-dropdown"
            selectSource="matches found"
        />
    );
};

export default AssigneeSelect;
